import React, { useState, useRef } from "react";
import { IDatabaseLineItem } from "../../LineItemDBCreationToolPage";
import { LineItemOrderObject } from "../../../types";
import CatGroupRow from "./CatGroupRow";
import CatGroupPanel from "./CatGroupPanel";

export type CatGroup = {
	cat: string;
	items: LineItemOrderObject[];
};

export interface CatOrderTableProps {
	catGroups: CatGroup[];
	lineItems: IDatabaseLineItem[];
	onApply: (reorderedItems: LineItemOrderObject[]) => void;
	onCancel: () => void;
	isUpdating: boolean;
}

export type DragState =
	| { type: "group"; draggedCat: string }
	| { type: "item"; draggedItems: LineItemOrderObject[]; sourceCat: string }
	| null;

type ConfirmDropData =
	| { type: "group"; draggedCat: string; targetCat: string }
	| { type: "item"; draggedItems: LineItemOrderObject[]; targetCat: string; targetItem: LineItemOrderObject | null };

const CatOrderTable: React.FC<CatOrderTableProps> = ({ catGroups: initialCatGroups, lineItems, onApply, onCancel, isUpdating }) => {
	const [localGroups, setLocalGroups] = useState<CatGroup[]>(initialCatGroups);
	const [dragState, setDragState] = useState<DragState>(null);
	const [confirmDropData, setConfirmDropData] = useState<ConfirmDropData | null>(null);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const lastSelectedIdRef = useRef<string | null>(null);

	const getLineItemById = (id: string): IDatabaseLineItem | undefined => lineItems.find((li) => li.id === id);

	// ── Selection ──────────────────────────────────────────────────────────

	const handleItemClick = (e: React.MouseEvent, item: LineItemOrderObject) => {
		const id = item.lineItemId;
		const allItems = localGroups.flatMap((g) => g.items);

		if (e.shiftKey && lastSelectedIdRef.current) {
			const lastIdx = allItems.findIndex((i) => i.lineItemId === lastSelectedIdRef.current);
			const currentIdx = allItems.findIndex((i) => i.lineItemId === id);
			const [start, end] = [Math.min(lastIdx, currentIdx), Math.max(lastIdx, currentIdx)];
			const rangeIds = allItems.slice(start, end + 1).map((i) => i.lineItemId);
			setSelectedIds((prev) => new Set([...Array.from(prev), ...rangeIds]));
		} else {
			setSelectedIds((prev) => {
				const next = new Set(prev);
				if (next.has(id)) next.delete(id);
				else next.add(id);
				return next;
			});
			lastSelectedIdRef.current = id;
		}
	};

	// ── Group drag handlers ────────────────────────────────────────────────

	const handleGroupDragStart = (e: React.DragEvent, cat: string) => {
		setDragState({ type: "group", draggedCat: cat });
		e.dataTransfer.effectAllowed = "move";
	};

	const handleGroupDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
	};

	const handleGroupDrop = (e: React.DragEvent, targetCat: string) => {
		e.preventDefault();
		if (!dragState || dragState.type !== "group") return;
		if (dragState.draggedCat === targetCat) return;
		setConfirmDropData({ type: "group", draggedCat: dragState.draggedCat, targetCat });
	};

	// ── Item drag handlers ─────────────────────────────────────────────────

	const handleItemDragStart = (e: React.DragEvent, item: LineItemOrderObject, sourceCat: string) => {
		const allItems = localGroups.flatMap((g) => g.items);
		const draggedItems = selectedIds.has(item.lineItemId)
			? allItems.filter((i) => selectedIds.has(i.lineItemId))
			: [item];
		setDragState({ type: "item", draggedItems, sourceCat });
		e.dataTransfer.effectAllowed = "move";
		e.stopPropagation();
	};

	const handleItemDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		e.stopPropagation();
		e.dataTransfer.dropEffect = "move";
	};

	const handleItemDrop = (e: React.DragEvent, targetItem: LineItemOrderObject, targetCat: string) => {
		e.preventDefault();
		e.stopPropagation();
		if (!dragState || dragState.type !== "item") return;
		if (dragState.draggedItems.some((i) => i.lineItemId === targetItem.lineItemId)) return;
		setConfirmDropData({ type: "item", draggedItems: dragState.draggedItems, targetCat, targetItem });
	};

	// Drop item onto a group header → append to that group
	const handleItemDropOnHeader = (e: React.DragEvent, targetCat: string) => {
		e.preventDefault();
		e.stopPropagation();
		if (!dragState || dragState.type !== "item") return;
		const allInTargetCat = dragState.draggedItems.every((item) => {
			const group = localGroups.find((g) => g.items.some((i) => i.lineItemId === item.lineItemId));
			return group?.cat === targetCat;
		});
		if (allInTargetCat) return;
		setConfirmDropData({ type: "item", draggedItems: dragState.draggedItems, targetCat, targetItem: null });
	};

	const handleDragEnd = () => setDragState(null);

	// ── Confirm / cancel ───────────────────────────────────────────────────

	const confirmDrop = () => {
		if (!confirmDropData) return;

		let newGroups: CatGroup[];

		if (confirmDropData.type === "group") {
			const { draggedCat, targetCat } = confirmDropData;
			newGroups = [...localGroups];
			const draggedIndex = newGroups.findIndex((g) => g.cat === draggedCat);
			const targetIndex = newGroups.findIndex((g) => g.cat === targetCat);
			const [removed] = newGroups.splice(draggedIndex, 1);
			newGroups.splice(targetIndex, 0, removed);
		} else {
			const { draggedItems, targetCat, targetItem } = confirmDropData;
			const draggedIds = new Set(draggedItems.map((i) => i.lineItemId));

			// Remove dragged items from all groups
			newGroups = localGroups.map((g) => ({
				...g,
				items: g.items.filter((i) => !draggedIds.has(i.lineItemId)),
			}));

			// Insert into target group
			const targetGroup = newGroups.find((g) => g.cat === targetCat)!;
			if (targetItem === null) {
				targetGroup.items.push(...draggedItems);
			} else {
				const targetIdx = targetGroup.items.findIndex((i) => i.lineItemId === targetItem.lineItemId);
				targetGroup.items.splice(targetIdx, 0, ...draggedItems);
			}

			setSelectedIds(new Set());
		}

		setLocalGroups(newGroups);
		setDragState(null);
		setConfirmDropData(null);
	};

	const cancelDrop = () => {
		setConfirmDropData(null);
		setDragState(null);
	};

	// ── Flatten helper ─────────────────────────────────────────────────────

	const flattenGroups = (groups: CatGroup[]): LineItemOrderObject[] =>
		groups.flatMap((g) => g.items).map((item, index) => ({ ...item, index }));

	// ── Apply ──────────────────────────────────────────────────────────────

	const handleApply = () => {
		onApply(flattenGroups(localGroups));
	};

	// ── Render ─────────────────────────────────────────────────────────────

	const isDraggingGroup = dragState?.type === "group";
	const isDraggingItem = dragState?.type === "item";
	const draggedCat = dragState?.type === "group" ? dragState.draggedCat : null;

	return (
		<>
			<div className="flex justify-end gap-3 mb-4">
				<button
					onClick={onCancel}
					className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
				>
					Cancel
				</button>
				<button
					onClick={handleApply}
					disabled={isUpdating}
					className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
				>
					{isUpdating ? "Saving..." : "Apply Order"}
				</button>
			</div>

			{selectedIds.size > 0 && (
				<div className="mb-3 flex items-center gap-3 text-sm text-blue-700">
					<span>{selectedIds.size} item{selectedIds.size > 1 ? "s" : ""} selected</span>
					<button onClick={() => setSelectedIds(new Set())} className="text-xs underline hover:no-underline">
						Clear selection
					</button>
				</div>
			)}

			<div className="flex gap-4 items-start">
				<div className="flex-1 bg-white rounded-lg shadow">
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Index</th>
								<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Line Item</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{localGroups.map((group) => {
								const groupStartIndex = localGroups
									.slice(0, localGroups.indexOf(group))
									.reduce((acc, g) => acc + g.items.length, 0);

								return (
									<CatGroupRow
										key={group.cat}
										group={group}
										groupStartIndex={groupStartIndex}
										isDraggingGroup={isDraggingGroup}
										isDraggingItem={isDraggingItem}
										dragState={dragState}
										selectedIds={selectedIds}
										getLineItemById={getLineItemById}
										onGroupDragStart={handleGroupDragStart}
										onGroupDragOver={handleGroupDragOver}
										onGroupDrop={handleGroupDrop}
										onItemDropOnHeader={handleItemDropOnHeader}
										onDragEnd={handleDragEnd}
										onItemDragStart={handleItemDragStart}
										onItemDragOver={handleItemDragOver}
										onItemDrop={handleItemDrop}
										onItemClick={handleItemClick}
									/>
								);
							})}
						</tbody>
					</table>
				</div>
				<CatGroupPanel
					groups={localGroups}
					draggedCat={draggedCat}
					onGroupDragStart={handleGroupDragStart}
					onGroupDragOver={handleGroupDragOver}
					onGroupDrop={handleGroupDrop}
					onDragEnd={handleDragEnd}
				/>
			</div>

			{/* Confirmation Modal */}
			{confirmDropData && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
						<h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Move</h3>
						<p className="text-gray-700 mb-6">
							{confirmDropData.type === "group" ? (
								<>
									Move all items in{" "}
									<span className="font-semibold">"{confirmDropData.draggedCat}"</span>{" "}
									before{" "}
									<span className="font-semibold">"{confirmDropData.targetCat}"</span>?
								</>
							) : (
								<>
									Move{" "}
									{confirmDropData.draggedItems.length === 1 ? (
										<span className="font-semibold">
											"{getLineItemById(confirmDropData.draggedItems[0].lineItemId)?.originalLineItem || confirmDropData.draggedItems[0].lineItemId}"
										</span>
									) : (
										<span className="font-semibold">{confirmDropData.draggedItems.length} items</span>
									)}{" "}
									{confirmDropData.targetItem === null ? (
										<>
											into{" "}
											<span className="font-semibold">"{confirmDropData.targetCat}"</span> at the end of the group
										</>
									) : (
										<>
											right above{" "}
											<span className="font-semibold">
												"{getLineItemById(confirmDropData.targetItem.lineItemId)?.originalLineItem || confirmDropData.targetItem.lineItemId}"
											</span>
											{" "}in <span className="font-semibold">"{confirmDropData.targetCat}"</span>
										</>
									)}
									?
								</>
							)}
						</p>
						<div className="flex justify-end gap-3">
							<button onClick={cancelDrop} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors">
								Cancel
							</button>
							<button onClick={confirmDrop} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
								Confirm
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default CatOrderTable;
